import {Coupon} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/coupon.types';
import {useManualTimeout} from '@aboutyou/ui-components';
import {useEffect, useMemo, useRef} from 'react';
import {useActivatedCouponState} from '../../../../../../features/Coupons/useActivatedCouponState';
import {useCouponStickyBarOpenStateManager} from '../../CouponStickyBar/utils/useCouponStickyBarOpenStateManager';

export const useCouponBadgeActive = ({
  productId,
  isInMiniADPDialog,
}: {
  productId: number;
  isInMiniADPDialog: boolean;
}) => {
  const previousActivatedCoupon = useRef<Coupon | null>(null);
  const {activatedCoupon, headline} = useActivatedCouponState(productId);
  const {isOpen: isCouponStickyBarOpen} =
    useCouponStickyBarOpenStateManager(isInMiniADPDialog);
  const [countdownDelay, startCountdownDelay] = useManualTimeout(1000);
  const countdownAnimationDone = useRef(false);

  useEffect(() => {
    if (
      !isCouponStickyBarOpen &&
      activatedCoupon &&
      previousActivatedCoupon.current !== activatedCoupon
    ) {
      startCountdownDelay();
      previousActivatedCoupon.current = activatedCoupon;
    }
  }, [activatedCoupon, isCouponStickyBarOpen, startCountdownDelay]);

  useEffect(() => {
    countdownAnimationDone.current = Boolean(activatedCoupon && countdownDelay);
  }, [countdownDelay, activatedCoupon]);

  // the badge should be activated when:
  // - the user activate a coupon and close the CouponStickyBar or
  // - a coupon was previously activated
  const canShowBadge = useMemo(() => {
    if (!activatedCoupon) {
      return false;
    }

    if (countdownAnimationDone.current) {
      return true;
    }

    return !isCouponStickyBarOpen;
  }, [activatedCoupon, isCouponStickyBarOpen]);

  const canShowCountdown = countdownAnimationDone.current
    ? true
    : countdownDelay;

  return {activatedCoupon, canShowBadge, canShowCountdown, headline};
};
